import { TFunction } from 'i18next'

import { intlLocaleForUi } from '@/i18n'

import { CitationFigure } from './citationSource'

/**
 * Unit code → decimals. Matches `AnalystTab/formatAnalystValue.ts` (percent
 * 1, counts 0, everything else 2) and its unit aliases (Analyst facts may say
 * `%`), so one fact reads the same in an Analyst chart, the pill, the card and
 * the Analyst evidence rows.
 */
const PERCENT_UNITS = new Set(['percent', '%', 'percentage', 'pct'])
const WHOLE_NUMBER_UNITS = new Set([
  'count', 'counts', 'number', 'base', 'n', 'people', 'respondents', 'responses', 'sample',
])

const DEFAULT_FRACTION_DIGITS = 2

/**
 * The single formatter for every citation surface. Returns the value only,
 * localised; the unit code itself is never shown as text (`p_value`, `mean`,
 * `index` and any unknown code render as the plain number).
 */
export function formatFigure(figure: CitationFigure, t: TFunction<'app'>, locale: string): string {
  const unit = (figure.unit || '').trim().toLowerCase()
  const isPercent = PERCENT_UNITS.has(unit)
  const maximumFractionDigits = isPercent ? 1 : WHOLE_NUMBER_UNITS.has(unit) ? 0 : DEFAULT_FRACTION_DIGITS
  const value = new Intl.NumberFormat(intlLocaleForUi(locale), { maximumFractionDigits }).format(figure.value)

  if (isPercent) {
    return t('threads.detail.sourcePill.figurePercent', { value })
  }
  return value
}

/** "In person only: 83.4%". */
export function formatFigureLine(figure: CitationFigure, t: TFunction<'app'>, locale: string): string {
  const value = formatFigure(figure, t, locale)
  const label = figure.label.trim()
  if (!label) {
    return value
  }
  return t('threads.detail.sourcePill.figureLine', { label, value })
}
